import _jsx from "@babel/runtime/helpers/jsx";
import clsx from 'clsx';
import events from 'dom-helpers/events';
import ownerDocument from 'dom-helpers/ownerDocument';
import canUseDOM from 'dom-helpers/util/inDOM';
import getScrollbarSize from 'dom-helpers/util/scrollbarSize';
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import BaseModal from 'react-overlays/lib/Modal';
import isOverflowing from 'react-overlays/lib/utils/isOverflowing';
import elementType from 'prop-types-extra/lib/elementType';
import componentOrElement from 'prop-types-extra/lib/componentOrElement';
import deprecated from 'prop-types-extra/lib/deprecated';
import Fade from '../Fade/Fade.js';
import Body from '../ModalBody/ModalBody.js';
import ModalDialog from './ModalDialog';
import Footer from '../ModalFooter/ModalFooter.js';
import Header from './ModalHeader';
import Title from './ModalTitle';
import createChainedFunction from './utils/createChainedFunction';
import splitComponentProps from './utils/splitComponentProps';
import { Size } from './utils/StyleConfig';
import ModalContext from './ModalContext';

/* eslint-disable no-use-before-define */
function DialogTransition(props) {
  return /*#__PURE__*/React.createElement(Fade, Object.assign({}, props, {
    timeout: Modal.TRANSITION_DURATION
  }));
}
function BackdropTransition(props) {
  return /*#__PURE__*/React.createElement(Fade, Object.assign({}, props, {
    timeout: Modal.BACKDROP_TRANSITION_DURATION
  }));
}

/* eslint-enable no-use-before-define */

function Modal(props) {
  const {
    backdrop = true,
    backdropClassName,
    animation = true,
    show = false,
    dialogComponentClass: Dialog = ModalDialog,
    className,
    style,
    children,
    onEntering,
    onExited,
    onHide = () => {},
    bsClass = 'modal',
    bsSize,
    ...otherProps
  } = props;
  const [modalStyle, setModalStyle] = useState({});
  const modalRef = useRef(null);
  const {
    container
  } = props;

  // State for tracking modal interactions
  const ignoreBackdropClickRef = useRef(false);
  const waitingForMouseUpRef = useRef(false);
  const handleDialogClick = e => {
    if (ignoreBackdropClickRef.current || e.target !== e.currentTarget) {
      ignoreBackdropClickRef.current = false;
      return;
    }
    onHide();
  };
  const updateStyle = useCallback(() => {
    if (!canUseDOM || !modalRef.current) {
      return;
    }
    const dialogNode = modalRef.current.getDialogElement();
    const dialogHeight = dialogNode.scrollHeight;
    const document = ownerDocument(dialogNode);
    const bodyIsOverflowing = isOverflowing(container || document.body);
    const modalIsOverflowing = dialogHeight > document.documentElement.clientHeight;
    setModalStyle({
      paddingRight: bodyIsOverflowing && !modalIsOverflowing ? getScrollbarSize() : undefined,
      paddingLeft: !bodyIsOverflowing && modalIsOverflowing ? getScrollbarSize() : undefined
    });
  }, [container]);
  const handleWindowResize = useCallback(() => {
    updateStyle();
  }, [updateStyle]);
  const handleEntering = () => {
    // FIXME: This should work even when animation is disabled.
    events.on(window, 'resize', handleWindowResize);
    updateStyle();
  };
  const handleExited = useCallback(() => {
    // FIXME: This should work even when animation is disabled.
    events.off(window, 'resize', handleWindowResize);

    // Ensure the modal-open class is removed from the body to restore scrolling
    if (document.body.classList.contains('modal-open')) {
      document.body.classList.remove('modal-open');
    }
  }, [handleWindowResize]);
  const setModalRefHandler = ref => {
    modalRef.current = ref;
  };
  const handleDialogBackdropMouseDown = () => {
    waitingForMouseUpRef.current = true;
  };
  const handleMouseUp = ev => {
    if (!modalRef.current) return;
    const dialogNode = modalRef.current.getDialogElement();
    if (waitingForMouseUpRef.current && ev.target === dialogNode) {
      ignoreBackdropClickRef.current = true;
    }
    waitingForMouseUpRef.current = false;
  };

  // Cleanup on unmount
  useEffect(() => () => {
    handleExited();
  }, [handleExited]);
  const [baseModalProps, dialogProps] = splitComponentProps({
    ...otherProps,
    bsClass,
    bsSize
  }, BaseModal);
  const inClassName = show && !animation && 'in';

  // Create the context value
  const modalContextValue = useMemo(() => ({
    onHide
  }), [onHide]);
  return /*#__PURE__*/_jsx(ModalContext.Provider, {
    value: modalContextValue
  }, void 0, /*#__PURE__*/React.createElement(BaseModal, Object.assign({}, baseModalProps, {
    ref: setModalRefHandler,
    show: show,
    containerClassName: "modal-open",
    transition: animation ? DialogTransition : undefined,
    backdrop: backdrop,
    backdropTransition: animation ? BackdropTransition : undefined,
    backdropClassName: clsx('modal-backdrop', backdropClassName, inClassName),
    onEntering: createChainedFunction(onEntering, handleEntering),
    onExited: createChainedFunction(onExited, handleExited),
    onMouseUp: handleMouseUp
  }), /*#__PURE__*/React.createElement(Dialog, Object.assign({}, dialogProps, {
    style: {
      ...modalStyle,
      ...style
    },
    className: clsx(className, inClassName),
    onClick: backdrop === true ? handleDialogClick : null,
    onMouseDownDialog: handleDialogBackdropMouseDown
  }), children)));
}
Modal.Body = Body;
Modal.Header = Header;
Modal.Title = Title;
Modal.Footer = Footer;
Modal.Dialog = ModalDialog;
Modal.TRANSITION_DURATION = 300;
Modal.BACKDROP_TRANSITION_DURATION = 150;
export default Modal;